import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "../store/auth.store";
import { getConfig } from "../config";

export const api = axios.create();

api.interceptors.request.use((cfg) => {
  cfg.baseURL = getConfig().apiBaseUrl;
  const token = useAuthStore.getState().accessToken;
  if (token) {
    cfg.headers = cfg.headers ?? {};
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

let refreshing: Promise<string | null> | null = null;

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
    const status = error.response?.status;

    if (status === 401 && original && !original._retried) {
      original._retried = true;

      // Impersonation sessions have no refresh token. On 401, drop back to the
      // admin's session rather than clearing auth entirely.
      const state = useAuthStore.getState();
      if (state.impersonator) {
        state.stopImpersonating();
        const adminToken = useAuthStore.getState().accessToken;
        if (adminToken) {
          original.headers = original.headers ?? {};
          original.headers.Authorization = `Bearer ${adminToken}`;
          return api.request(original);
        }
        return Promise.reject(error);
      }

      refreshing ??= refreshAccessToken();
      const newToken = await refreshing;
      refreshing = null;

      if (newToken) {
        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      }
      useAuthStore.getState().clear();
    }
    return Promise.reject(error);
  },
);

async function refreshAccessToken(): Promise<string | null> {
  const { refreshToken, setTokens } = useAuthStore.getState();
  if (!refreshToken) return null;
  try {
    const { data } = await axios.post(`${getConfig().apiBaseUrl}/auth/refresh`, { refreshToken });
    setTokens({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      user: data.user,
    });
    return data.accessToken;
  } catch {
    return null;
  }
}
